import type { RfqExcelFormData } from './rfq-excel.types';

/** Statuses for deterministic master-data resolution (no fuzzy / LLM). */
export type RfqImportResolutionStatus =
  | 'matched'
  | 'unresolved'
  | 'skipped'
  | 'dependency_unresolved';

export interface RfqImportFieldResolution {
  field: string;
  importedValue: string;
  resolvedValue?: string;
  resolvedId?: string;
  status: RfqImportResolutionStatus;
  message?: string;
}

export interface RfqImportNamedEntity {
  _id: string;
  name: string;
}

export interface RfqImportPortEntity {
  _id: string;
  name: string;
  country: string;
}

export interface RfqImportPortAgentEntity {
  _id: string;
  companyName: string;
  address?: string;
  portId?: string;
}

/**
 * Departments selectable in Create RFQ UI.
 * Broader Mongo/Excel enums may exist — import only accepts UI-selectable values.
 */
export const RFQ_UI_SELECTABLE_DEPARTMENTS = [
  'engine',
  'deck',
  'cabin',
  'operations',
  'safety',
  'other',
] as const;

export type RfqUiSelectableDepartment = (typeof RFQ_UI_SELECTABLE_DEPARTMENTS)[number];

export interface RfqImportMasterLookup {
  categories: RfqImportNamedEntity[];
  vendors: RfqImportNamedEntity[];
  ports: RfqImportPortEntity[];
  vessels?: Array<{ _id: string; name: string; imoNumber?: string }>;

  /** Override UI department allow-list (defaults to RFQ_UI_SELECTABLE_DEPARTMENTS). */
  selectableDepartments?: readonly string[];

  /** Preloaded hierarchical maps (preferred for tests / offline). */
  subCategoriesByCategoryId?: Record<string, RfqImportNamedEntity[]>;
  brandsByCategoryId?: Record<string, RfqImportNamedEntity[]>;
  modelsByBrandId?: Record<string, RfqImportNamedEntity[]>;
  portAgentsByPortId?: Record<string, RfqImportPortAgentEntity[]>;

  /**
   * Flat agents list. Used only when already scoped to the resolved port
   * (or entries include portId for filtering). Prefer getPortAgentsForPort.
   */
  portAgents?: RfqImportPortAgentEntity[];

  /** Lazy loaders reusing existing GET APIs (never create). */
  getSubCategoriesForCategory?: (
    categoryId: string
  ) => Promise<RfqImportNamedEntity[]> | RfqImportNamedEntity[];
  getBrandsForCategory?: (
    categoryId: string
  ) => Promise<RfqImportNamedEntity[]> | RfqImportNamedEntity[];
  getModelsForBrand?: (
    brandId: string
  ) => Promise<RfqImportNamedEntity[]> | RfqImportNamedEntity[];
  getPortAgentsForPort?: (
    portId: string
  ) => Promise<RfqImportPortAgentEntity[]> | RfqImportPortAgentEntity[];
}

export interface ResolveRfqImportMasterDataInput {
  /** Provisional form patch (imported raw master-data values may be present). */
  formPatch: Partial<Omit<RfqExcelFormData, 'department'>> & {
    department?: string;
    portNameInput?: string;
    portAgentNameInput?: string;
    vendors?: string;
  } & Record<string, unknown>;
  /** Comma-separated vendor names when selectedVendorIds not yet set. */
  vendorNamesRaw?: string;
  lookup: RfqImportMasterLookup;
}

export interface ResolveRfqImportMasterDataResult {
  formPatch: Partial<RfqExcelFormData>;
  resolutions: RfqImportFieldResolution[];
  warnings: string[];
  unresolvedFields: string[];
}

/** Exact → trimmed CI → normalized key matching. */
export function normalizeMasterDataKey(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_./\\-]+/g, ' ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function findNamedEntityMatch<T extends { name: string }>(
  items: T[],
  imported: string
): T | undefined {
  if (!imported?.trim() || !items?.length) return undefined;

  const exact = items.find((i) => i.name === imported);
  if (exact) return exact;

  const trimmed = imported.trim();
  const caseInsensitive = items.find((i) => i.name.toLowerCase() === trimmed.toLowerCase());
  if (caseInsensitive) return caseInsensitive;

  const norm = normalizeMasterDataKey(imported);
  return items.find((i) => normalizeMasterDataKey(i.name) === norm);
}

function findAgentMatch(
  agents: RfqImportPortAgentEntity[],
  imported: string
): RfqImportPortAgentEntity | undefined {
  if (!imported?.trim() || !agents?.length) return undefined;

  const exact = agents.find((a) => a.companyName === imported);
  if (exact) return exact;

  const trimmed = imported.trim();
  const caseInsensitive = agents.find(
    (a) => a.companyName.toLowerCase() === trimmed.toLowerCase()
  );
  if (caseInsensitive) return caseInsensitive;

  const norm = normalizeMasterDataKey(imported);
  return agents.find((a) => normalizeMasterDataKey(a.companyName) === norm);
}

function splitVendorNames(value: string): string[] {
  return value
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function isUnknownToken(value: string): boolean {
  return normalizeMasterDataKey(value) === 'unknown';
}

async function resolveList<T>(
  preloaded: T[] | undefined,
  loader: (() => Promise<T[]> | T[]) | undefined
): Promise<T[]> {
  if (preloaded && preloaded.length > 0) return preloaded;
  if (!loader) return preloaded ?? [];
  return await loader();
}

function pushResolution(
  resolutions: RfqImportFieldResolution[],
  unresolvedFields: string[],
  warnings: string[],
  entry: RfqImportFieldResolution
): void {
  resolutions.push(entry);
  if (entry.status === 'unresolved' || entry.status === 'dependency_unresolved') {
    if (!unresolvedFields.includes(entry.field)) unresolvedFields.push(entry.field);
  }
  if (entry.message) warnings.push(entry.message);
}

/**
 * Resolve imported RFQ master-data values against existing ERP records.
 * Never creates categories, brands, models, vendors, ports, or agents.
 *
 * Dependency order: Category → Subcategory → Brand → Model;
 * Port → Port Agent.
 */
export async function resolveRfqImportMasterData(
  input: ResolveRfqImportMasterDataInput
): Promise<ResolveRfqImportMasterDataResult> {
  const { lookup } = input;
  const source = { ...input.formPatch } as Partial<RfqExcelFormData> & Record<string, unknown>;
  const formPatch: Partial<RfqExcelFormData> = { ...source };
  const resolutions: RfqImportFieldResolution[] = [];
  const warnings: string[] = [];
  const unresolvedFields: string[] = [];

  const selectableDepartments =
    lookup.selectableDepartments ?? RFQ_UI_SELECTABLE_DEPARTMENTS;

  // ——— Department ———
  const importedDepartment =
    typeof source.department === 'string' ? source.department.trim() : '';
  if (!importedDepartment) {
    delete formPatch.department;
  } else {
    const deptNorm = normalizeMasterDataKey(importedDepartment).replace(/\s+/g, '');
    const matchedDept = selectableDepartments.find(
      (d) => d === importedDepartment || normalizeMasterDataKey(d).replace(/\s+/g, '') === deptNorm
    );
    if (matchedDept) {
      formPatch.department = matchedDept as RfqExcelFormData['department'];
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'department',
        importedValue: importedDepartment,
        resolvedValue: matchedDept,
        status: 'matched',
      });
    } else {
      delete formPatch.department;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'department',
        importedValue: importedDepartment,
        status: 'unresolved',
        message: `Department "${importedDepartment}" is not selectable in Create RFQ — left empty`,
      });
    }
  }

  // ——— Category ———
  const importedCategory = typeof source.category === 'string' ? source.category.trim() : '';
  let resolvedCategory: RfqImportNamedEntity | undefined;
  if (!importedCategory) {
    delete formPatch.category;
  } else {
    resolvedCategory = findNamedEntityMatch(lookup.categories, importedCategory);
    if (resolvedCategory) {
      formPatch.category = resolvedCategory.name;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'category',
        importedValue: importedCategory,
        resolvedValue: resolvedCategory.name,
        resolvedId: resolvedCategory._id,
        status: 'matched',
      });
    } else {
      delete formPatch.category;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'category',
        importedValue: importedCategory,
        status: 'unresolved',
        message: `Category "${importedCategory}" not found in master data — left empty`,
      });
    }
  }

  // ——— Subcategory (requires category) ———
  const importedSubCategory =
    typeof source.subCategory === 'string' ? source.subCategory.trim() : '';
  if (!importedSubCategory) {
    delete formPatch.subCategory;
  } else if (!resolvedCategory) {
    delete formPatch.subCategory;
    pushResolution(resolutions, unresolvedFields, warnings, {
      field: 'subCategory',
      importedValue: importedSubCategory,
      status: importedCategory ? 'dependency_unresolved' : 'dependency_unresolved',
      message: `Sub Category "${importedSubCategory}" skipped — category must be resolved first`,
    });
  } else {
    const subs = await resolveList(
      lookup.subCategoriesByCategoryId?.[resolvedCategory._id],
      lookup.getSubCategoriesForCategory
        ? () => lookup.getSubCategoriesForCategory!(resolvedCategory!._id)
        : undefined
    );
    const matchedSub = findNamedEntityMatch(subs, importedSubCategory);
    if (matchedSub) {
      formPatch.subCategory = matchedSub.name;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'subCategory',
        importedValue: importedSubCategory,
        resolvedValue: matchedSub.name,
        resolvedId: matchedSub._id,
        status: 'matched',
      });
    } else {
      delete formPatch.subCategory;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'subCategory',
        importedValue: importedSubCategory,
        status: 'unresolved',
        message: `Sub Category "${importedSubCategory}" not found under category "${resolvedCategory.name}" — left empty`,
      });
    }
  }

  // ——— Brand (requires category; Unknown allowed) ———
  const importedBrand = typeof source.brand === 'string' ? source.brand.trim() : '';
  let resolvedBrand: RfqImportNamedEntity | { _id: ''; name: 'Unknown' } | undefined;
  if (!importedBrand) {
    delete formPatch.brand;
  } else if (!resolvedCategory) {
    delete formPatch.brand;
    pushResolution(resolutions, unresolvedFields, warnings, {
      field: 'brand',
      importedValue: importedBrand,
      status: 'dependency_unresolved',
      message: `Brand "${importedBrand}" skipped — category must be resolved first`,
    });
  } else if (isUnknownToken(importedBrand)) {
    formPatch.brand = 'Unknown';
    resolvedBrand = { _id: '', name: 'Unknown' };
    pushResolution(resolutions, unresolvedFields, warnings, {
      field: 'brand',
      importedValue: importedBrand,
      resolvedValue: 'Unknown',
      status: 'matched',
    });
  } else {
    const brands = await resolveList(
      lookup.brandsByCategoryId?.[resolvedCategory._id],
      lookup.getBrandsForCategory
        ? () => lookup.getBrandsForCategory!(resolvedCategory!._id)
        : undefined
    );
    const matchedBrand = findNamedEntityMatch(brands, importedBrand);
    if (matchedBrand) {
      formPatch.brand = matchedBrand.name;
      resolvedBrand = matchedBrand;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'brand',
        importedValue: importedBrand,
        resolvedValue: matchedBrand.name,
        resolvedId: matchedBrand._id,
        status: 'matched',
      });
    } else {
      delete formPatch.brand;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'brand',
        importedValue: importedBrand,
        status: 'unresolved',
        message: `Brand "${importedBrand}" not found under category "${resolvedCategory.name}" — left empty`,
      });
    }
  }

  // ——— Model (requires brand; Unknown allowed) ———
  const importedModel = typeof source.model === 'string' ? source.model.trim() : '';
  if (!importedModel) {
    delete formPatch.model;
  } else if (!resolvedBrand) {
    delete formPatch.model;
    pushResolution(resolutions, unresolvedFields, warnings, {
      field: 'model',
      importedValue: importedModel,
      status: 'dependency_unresolved',
      message: `Model "${importedModel}" skipped — brand must be resolved first`,
    });
  } else if (isUnknownToken(importedModel)) {
    formPatch.model = 'Unknown';
    pushResolution(resolutions, unresolvedFields, warnings, {
      field: 'model',
      importedValue: importedModel,
      resolvedValue: 'Unknown',
      status: 'matched',
    });
  } else if (resolvedBrand.name === 'Unknown') {
    delete formPatch.model;
    pushResolution(resolutions, unresolvedFields, warnings, {
      field: 'model',
      importedValue: importedModel,
      status: 'dependency_unresolved',
      message: `Model "${importedModel}" cannot be resolved when brand is Unknown — left empty`,
    });
  } else {
    const models = await resolveList(
      lookup.modelsByBrandId?.[resolvedBrand._id],
      lookup.getModelsForBrand
        ? () => lookup.getModelsForBrand!(resolvedBrand!._id)
        : undefined
    );
    const matchedModel = findNamedEntityMatch(models, importedModel);
    if (matchedModel) {
      formPatch.model = matchedModel.name;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'model',
        importedValue: importedModel,
        resolvedValue: matchedModel.name,
        resolvedId: matchedModel._id,
        status: 'matched',
      });
    } else {
      delete formPatch.model;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'model',
        importedValue: importedModel,
        status: 'unresolved',
        message: `Model "${importedModel}" not found under brand "${resolvedBrand.name}" — left empty`,
      });
    }
  }

  // ——— Vendors ———
  const namesToResolve =
    (input.vendorNamesRaw && input.vendorNamesRaw.trim()) ||
    (typeof source.vendors === 'string' ? String(source.vendors).trim() : '');

  if (namesToResolve) {
    const names = splitVendorNames(namesToResolve);
    const resolvedIds: string[] = [];
    for (const name of names) {
      const vendor = findNamedEntityMatch(lookup.vendors, name);
      if (vendor) {
        resolvedIds.push(vendor._id);
        pushResolution(resolutions, unresolvedFields, warnings, {
          field: 'vendors',
          importedValue: name,
          resolvedValue: vendor.name,
          resolvedId: vendor._id,
          status: 'matched',
        });
      } else {
        pushResolution(resolutions, unresolvedFields, warnings, {
          field: 'vendors',
          importedValue: name,
          status: 'unresolved',
          message: `Vendor "${name}" not found — skipped`,
        });
      }
    }
    if (resolvedIds.length > 0) {
      formPatch.selectedVendorIds = [...new Set(resolvedIds)];
    } else {
      delete formPatch.selectedVendorIds;
    }
  } else if (Array.isArray(source.selectedVendorIds) && source.selectedVendorIds.length > 0) {
    const valid = source.selectedVendorIds.filter((id) =>
      lookup.vendors.some((v) => v._id === id)
    );
    if (valid.length > 0) formPatch.selectedVendorIds = valid;
    else delete formPatch.selectedVendorIds;
  }

  // ——— Port ———
  const importedPort =
    (typeof source.portName === 'string' && source.portName.trim()) ||
    (typeof source.portNameInput === 'string' && String(source.portNameInput).trim()) ||
    '';
  let resolvedPort: RfqImportPortEntity | undefined;
  if (importedPort) {
    const portAsNamed = lookup.ports.map((p) => ({ _id: p._id, name: p.name }));
    const portMatch = findNamedEntityMatch(portAsNamed, importedPort);
    resolvedPort = portMatch
      ? lookup.ports.find((p) => p._id === portMatch._id)
      : undefined;

    if (resolvedPort) {
      formPatch.portId = resolvedPort._id;
      formPatch.portName = resolvedPort.name;
      formPatch.portCountry = resolvedPort.country;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'port',
        importedValue: importedPort,
        resolvedValue: resolvedPort.name,
        resolvedId: resolvedPort._id,
        status: 'matched',
      });
    } else {
      delete formPatch.portId;
      delete formPatch.portName;
      delete formPatch.portCountry;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'port',
        importedValue: importedPort,
        status: 'unresolved',
        message: `Port "${importedPort}" not found — left empty`,
      });
    }
  }

  // ——— Port Agent (requires port) ———
  const importedAgent =
    (typeof source.portAgentName === 'string' && source.portAgentName.trim()) ||
    (typeof (source as Record<string, unknown>).portAgentNameInput === 'string' &&
      String((source as Record<string, unknown>).portAgentNameInput).trim()) ||
    '';

  if (!importedAgent) {
    // no-op
  } else if (!resolvedPort) {
    delete formPatch.portAgentId;
    delete formPatch.portAgentName;
    delete formPatch.portAddress;
    pushResolution(resolutions, unresolvedFields, warnings, {
      field: 'portAgent',
      importedValue: importedAgent,
      status: 'dependency_unresolved',
      message: `Port agent "${importedAgent}" skipped — port must be resolved first`,
    });
  } else {
    let agents =
      lookup.portAgentsByPortId?.[resolvedPort._id] ??
      (await resolveList(undefined, lookup.getPortAgentsForPort
        ? () => lookup.getPortAgentsForPort!(resolvedPort!._id)
        : undefined));

    if ((!agents || agents.length === 0) && lookup.portAgents?.length) {
      agents = lookup.portAgents.filter(
        (a) => !a.portId || a.portId === resolvedPort!._id
      );
      // If none have portId, treat flat list as pre-scoped to this port (caller responsibility)
      if (agents.length === 0 && lookup.portAgents.every((a) => !a.portId)) {
        agents = lookup.portAgents;
      }
    }

    const matchedAgent = findAgentMatch(agents ?? [], importedAgent);
    if (matchedAgent) {
      formPatch.portAgentId = matchedAgent._id;
      formPatch.portAgentName = matchedAgent.companyName;
      formPatch.portAddress = matchedAgent.address ?? '';
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'portAgent',
        importedValue: importedAgent,
        resolvedValue: matchedAgent.companyName,
        resolvedId: matchedAgent._id,
        status: 'matched',
      });
    } else {
      delete formPatch.portAgentId;
      delete formPatch.portAgentName;
      delete formPatch.portAddress;
      pushResolution(resolutions, unresolvedFields, warnings, {
        field: 'portAgent',
        importedValue: importedAgent,
        status: 'unresolved',
        message: `Port agent "${importedAgent}" not found for port "${resolvedPort.name}" — left empty`,
      });
    }
  }

  // Strip temporary raw keys that aren't form fields
  delete (formPatch as Record<string, unknown>).portNameInput;
  delete (formPatch as Record<string, unknown>).portAgentNameInput;
  delete (formPatch as Record<string, unknown>).vendors;

  return { formPatch, resolutions, warnings, unresolvedFields };
}
